/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Issue Operations
 * 
 * This module handles issue-related API operations including:
 * - Fetching issues markdown content
 * - Updating issue status
 * - Updating issue fields (description, plan, links)
 * - Finding files in the codebase
 */

import { getProductServerBaseUrl, buildProductApiUrl } from './base';
import type {
  IssuesMarkdownResult,
  UpdateIssueResult,
  FindFileResult,
} from '../../../server/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Context for issue API calls
 */
export interface IssueRepoContext {
  repoUrl?: string | null;
  repoPath?: string | null;
}

// ============================================================================
// Issue Operations
// ============================================================================

/**
 * Fetch issues markdown content
 * 
 * @param repoContext - Repository context for the product
 * @returns Markdown content string
 */
export async function getIssuesMarkdown(
  repoContext?: IssueRepoContext
): Promise<IssuesMarkdownResult> {
  try {
    const params: Record<string, string> = {};
    if (repoContext?.repoUrl) {
      params.repoUrl = repoContext.repoUrl;
    }
    if (repoContext?.repoPath) {
      params.repoPath = repoContext.repoPath;
    }
    
    const apiUrl = buildProductApiUrl('/api/data/issues', params);

    const response = await fetch(apiUrl);

    if (!response.ok) {
      if (response.status === 404) {
        // 404 means directory/files don't exist yet
        return {
          success: true,
          content: ''
        };
      }
      return {
        success: false,
        error: `Failed to fetch issues (${response.status})`
      };
    }

    const content = await response.text();
    return {
      success: true,
      content
    };
  } catch (error: any) {
    console.error('[Product API] Error fetching issues markdown:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch issues'
    };
  }
}

/**
 * Update an issue's status
 * 
 * @param productId - Product ID
 * @param issueCode - Issue code (e.g., "BUG-005")
 * @param status - New status value
 * @param repoContext - Repository context for the product
 * @returns Update result
 */
export async function updateIssueStatus(
  productId: string,
  issueCode: string,
  status: string,
  repoContext?: IssueRepoContext
): Promise<UpdateIssueResult> {
  try {
    const params: Record<string, string> = {};
    if (repoContext?.repoUrl) {
      params.repoUrl = repoContext.repoUrl;
    }
    if (repoContext?.repoPath) {
      params.repoPath = repoContext.repoPath;
    }
    
    const apiUrl = buildProductApiUrl(
      `/api/issues/${encodeURIComponent(productId)}/${encodeURIComponent(issueCode)}/status`,
      params
    );

    const response = await fetch(apiUrl, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to update issue status (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error updating issue status:', error);
    return {
      success: false,
      error: error.message || 'Failed to update issue status'
    };
  }
}

/**
 * Update an issue's fields
 * 
 * @param productId - Product ID (e.g., "velocity", "skillshop")
 * @param issueId - Issue ID (e.g., "BUG-005")
 * @param updates - Fields to update (any valid issue fields)
 * @param repoContext - Repository context for the product
 * @returns Update result
 */
export async function updateIssue(
  productId: string,
  issueId: string,
  updates: Record<string, unknown>,
  repoContext?: IssueRepoContext
): Promise<UpdateIssueResult> {
  try {
    const params: Record<string, string> = {};
    if (repoContext?.repoUrl) {
      params.repoUrl = repoContext.repoUrl;
    }
    if (repoContext?.repoPath) {
      params.repoPath = repoContext.repoPath;
    }
    
    const apiUrl = buildProductApiUrl(
      `/api/issues/${encodeURIComponent(productId)}/${encodeURIComponent(issueId)}`,
      params
    );

    const response = await fetch(apiUrl, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to update issue (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error updating issue:', error);
    return {
      success: false,
      error: error.message || 'Failed to update issue'
    };
  }
}

/**
 * Delete an issue from issues.md
 *
 * @param productId - Product ID (UUID from active product)
 * @param issueId - Issue code (e.g., "BUG-005")
 * @param repoContext - Repository context for the product
 */
export async function deleteIssue(
  productId: string,
  issueId: string,
  repoContext?: IssueRepoContext
): Promise<UpdateIssueResult> {
  try {
    const params: Record<string, string> = {};
    if (repoContext?.repoUrl) {
      params.repoUrl = repoContext.repoUrl;
    }
    if (repoContext?.repoPath) {
      params.repoPath = repoContext.repoPath;
    }

    const apiUrl = buildProductApiUrl(
      `/api/issues/${encodeURIComponent(productId)}/${encodeURIComponent(issueId)}`,
      params
    );

    const response = await fetch(apiUrl, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error:
          (errorData as { error?: string }).error || `Failed to delete issue (${response.status})`,
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error deleting issue:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete issue'
    };
  }
}

/**
 * Find a file in the codebase by name
 * 
 * @param fileName - Name of the file to find
 * @returns File path if found
 */
export async function findFile(fileName: string): Promise<FindFileResult> {
  try {
    const apiUrl = `${getProductServerBaseUrl()}/api/find-file/${encodeURIComponent(fileName)}`;

    const response = await fetch(apiUrl);

    if (!response.ok) {
      return {
        success: false,
        found: false,
        error: `Failed to find file (${response.status})`
      };
    }

    const result = await response.json();
    return {
      success: true,
      found: result.found || false,
      path: result.path
    };
  } catch (error: any) {
    console.error('[Product API] Error finding file:', error);
    return {
      success: false,
      found: false,
      error: error.message || 'Failed to find file'
    };
  }
}

